// tslint:disable: variable-name
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ApmService } from '@elastic/apm-rum-angular';
import { Subject } from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { DataKlinik, DataKlinikV2 } from '../models/data-klinik.model';
import { IdbStorageService } from './storage/idb-storage.service';
import { storageDict } from './storage/storage-dict';

@Injectable({
  providedIn: 'root',
})
export class ClinicEnvironmentService {
  /**
   * Works as a cache/buffer stored in service, to get directly from the storage will be tiring,
   * since the operation is a promise/asynchronous.
   */
  private _clinic: DataKlinikV2;
  private _listenerInitialized = false;

  public authState$ = new Subject<boolean>();
  public clinicSet$ = new Subject<any>();

  constructor(
    private apmService: ApmService,
    private storage: IdbStorageService,
    private title: Title
  ) {
    // this.clinicSet$.subscribe((res) => console.log('clinicSet', res));
    this.storage.getItem(storageDict.klinik).then((clinic) => {
      if (clinic) {
        this.title.setTitle(clinic.nama);
        this._clinic = clinic;
        this.clinicSet$.next(clinic);
      }
      this.initService();
    });
  }

  private initService() {
    if (!this._listenerInitialized) {
      this._listenerInitialized = true;
      this.authState$.subscribe((authed) => {
        if (!this._clinic?.id) {
          this.storage.getItem(storageDict.klinik).then((clinic) => {
            if (clinic) {
              this._clinic = clinic;
              this.clinicSet$.next(clinic);
            }
          });
        }
      });
    }
  }

  /**
   * Get current clinic ID from the service.
   */
  public get clinicId() {
    if (!this._clinic) return null;
    return this._clinic.id;
  }

  public get clinicUuid() {
    if (!this._clinic) return null;
    return this._clinic.uuid;
  }

  public get clinicCode() {
    if (!this._clinic) return null;
    return this._clinic.kodeDataKlinik;
  }

  public get clinicPhone() {
    if (!this._clinic) return null;
    return this._clinic.telepon1 || this._clinic.telepon2;
  }

  public get clinicGroupId() {
    if (!this._clinic) return null;
    return this._clinic.idGroupDataKlinik;
  }

  public get clinicType() {
    if (!this._clinic) return null;
    return this._clinic.typePrice;
  }

  public get clinicTimeZone() {
    if (!this._clinic) return null;
    return this._clinic.timeZone;
  }

  public get clinicPotongStock() {
    if (!this._clinic) return null;
    return this._clinic.pemotonganStok;
  }

  public get clinicStatusBpjs() {
    if (!this._clinic) return null;
    return this._clinic.statusBpjs;
  }

  public get clinicHargaResep() {
    if (!this._clinic) return null;
    return this._clinic.statusHargaResep;
  }

  public get clinicKodePpk() {
    if (!this._clinic) return null;
    return this._clinic.kodePpkBpjs;
  }

  public get clinicStatusHapusKunjungan() {
    if (!this._clinic) return null;
    return this._clinic.statusHapusKunjungan;
  }

  public get clinicStatusStockHabisResep() {
    if (!this._clinic) return null;
    return this._clinic.statusStockHabisResep;
  }

  public get clinicStatusStockRiwayatResep() {
    if (!this._clinic) return null;
    return this._clinic.statusStockRiwayatResep;
  }

  public get clinicStatusCekSipResep() {
    if (!this._clinic) return null;
    return this._clinic.statusCekSipResep;
  }

  public get clinicStatusOrderAntrianMedis() {
    if (!this._clinic) return null;
    return this._clinic.statusOrderAntrianMedis;
  }

  public get clinicStatusFornasResep() {
    if (!this._clinic) return null;
    return this._clinic.statusFornasResep;
  }

  public get clinicConsIdBpjs() {
    if (!this._clinic) return null;
    return this._clinic.consIdBpjs;
  }

  public get clinicPasswordBpjs() {
    if (!this._clinic) return null;
    return this._clinic.passwordBpjs;
  }

  public get clinicUserKeyBpjs() {
    if (!this._clinic) return null;
    return this._clinic.userKeyBpjs;
  }

  public get clinicUrlBpjs() {
    if (!this._clinic) return null;
    return this._clinic.urlBpjs;
  }

  public get clinicStickyHeaderMedis() {
    if (!this._clinic) return null;
    return this._clinic.statusStickyHeaderMedis;
  }

  public get clinicStatusTambahCssd() {
    if (!this._clinic) return null;
    return this._clinic.statusTambahCssd;
  }

  public get clinicStatusInputDiagnosaText() {
    if (!this._clinic) return null;
    return this._clinic.statusInputDiagnosaText;
  }

  public get clinicPendaftaranDisabled() {
    if (!this._clinic) return null;
    return this._clinic.pendaftaranDisabled;
  }

  public get clinicPendaftaranDisabledException() {
    if (!this._clinic) return null;
    return this._clinic.pendaftaranDisabledException;
  }

  public get clinicName() {
    if (!this._clinic) return null;
    return this._clinic.nama;
  }

  public get clinicCicilan() {
    if (!this._clinic) return null;
    return this._clinic.cicilan;
  }

  public get clinicSharingPasien() {
    if (!this._clinic) return null;
    return this._clinic.sharingPasien;
  }

  public async getClinicIdAsync(): Promise<number> {
    if (this._clinic?.id) return this._clinic.id;

    const clinic = await this.clinicSet$.pipe(take(1), timeout(5000)).toPromise();
    return clinic.id;
  }

  public async getClinicAsync(): Promise<DataKlinikV2> {
    if (this._clinic) return this._clinic as DataKlinikV2;

    const clinic = await this.clinicSet$.pipe(take(1), timeout(5000)).toPromise();
    return clinic;
  }

  public async setClinic(val: DataKlinikV2) {
    await this.storage.setItem(storageDict.klinik, val);
    if (this.apmService?.apm) this.apmService.apm.setCustomContext({ idDataKlinik: val.id });
    this._clinic = val;
    this.clinicSet$.next(val);
  }
}
